import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updatePartner } from '../../../services/PartnerService';
import { showMutationError, makeFieldErrorClearer } from '../../../utils/showMutationError';

const EditPartnerModal = ({ show, onHide, partner }) => {
    const queryClient = useQueryClient();

    const [name, setName] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (show) {
            setName(partner?.name ?? '');
            setFieldErrors({});
        }
    }, [show, partner]);

    const clearFieldError = makeFieldErrorClearer(setFieldErrors);

    const mutation = useMutation({
        mutationFn: () => updatePartner(partner.id, { name: name.trim() }),
        onSuccess: (response) => {
            toast.success(response.data?.message || 'Partner updated successfully.');
            queryClient.invalidateQueries({ queryKey: ['partners'] });
            queryClient.invalidateQueries({ queryKey: ['partnerDetails', partner.id] });
            onHide();
        },
        onError: (err) => showMutationError(err, {
            fallback: 'Failed to update partner.',
            onFieldErrors: setFieldErrors,
        }),
    });

    const trimmedName = name.trim();
    const isUnchanged = trimmedName === (partner?.name ?? '').trim();
    const isSubmittable = trimmedName.length > 0 && !isUnchanged;

    const handleSubmit = (e) => {
        e.preventDefault();
        setFieldErrors({});
        mutation.mutate();
    };

    const inputClass = fieldErrors.name ? 'form-control is-invalid' : 'form-control';

    return (
        <Modal show={show} onHide={onHide} centered>
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title">Edit Partner</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={onHide}
                        disabled={mutation.isPending}
                    />
                </div>
                <form onSubmit={handleSubmit} noValidate>
                    <div className="modal-body">
                        <div className="form-group mb-3">
                            <label htmlFor="editPartnerName">Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={inputClass}
                                id="editPartnerName"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    clearFieldError('name');
                                }}
                                autoFocus
                            />
                            {fieldErrors.name && (
                                <div className="invalid-feedback d-block">{fieldErrors.name}</div>
                            )}
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onHide}
                            disabled={mutation.isPending}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!isSubmittable || mutation.isPending}
                        >
                            {mutation.isPending && <i className="fa fa-spinner fa-spin me-1" />}
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default EditPartnerModal;
